import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import type { Me } from '../../api/types';
import { absoluteDate } from '../../shared/format';
import { IconCheckCircle, IconInfo } from '../../shared/icons';

export function AccountPage({ me }: { me: Me }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const change = useMutation({
    mutationFn: () => api.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    change.mutate();
  };

  const error = change.error instanceof ApiError ? change.error : null;

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div className="panel" style={{ width: 380 }}>
        <div className="panel-hdr">
          <span>Your account</span>
        </div>
        <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Row label="Name" value={me.user.display_name} />
          <Row label="Email" value={me.user.email} />
          <Row label="Company" value={me.user.company || '—'} />
          <Row label="Session expires" value={absoluteDate(me.session.expires_at)} />
        </div>
      </div>

      <div className="panel" style={{ width: 380 }}>
        <div className="panel-hdr">
          <span>Change password</span>
        </div>
        <form
          className="panel-body"
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          onSubmit={handleSubmit}
        >
          {change.isSuccess && (
            <div className="notice notice-ok" role="status">
              <IconCheckCircle size={14} />
              <span>Password changed. Your other devices have been signed out.</span>
            </div>
          )}
          {error && (
            <div className="notice notice-error" role="alert">
              <IconInfo size={14} />
              <span>{error.message}</span>
            </div>
          )}
          {mismatch && (
            <div className="notice notice-error" role="alert">
              <IconInfo size={14} />
              <span>The two new passwords do not match.</span>
            </div>
          )}

          <div className="field">
            <label className="lbl" htmlFor="current-password">
              <span className="lbl-text">Current password</span>
            </label>
            <input
              id="current-password"
              className="inp inp-compact"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="lbl" htmlFor="new-password">
              <span className="lbl-text">New password</span>
            </label>
            <input
              id="new-password"
              className="inp inp-compact"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <span className="field-help">At least 12 characters.</span>
          </div>

          <div className="field">
            <label className="lbl" htmlFor="confirm-password">
              <span className="lbl-text">Confirm new password</span>
            </label>
            <input
              id="confirm-password"
              className={`inp inp-compact${mismatch ? ' inp-invalid' : ''}`}
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <button className="btn btn-primary btn-block" type="submit" disabled={change.isPending}>
            {change.isPending ? 'Saving…' : 'Change password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span className="cell-secondary">{label}</span>
      <span className="cell-primary" style={{ textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}
